import { useEffect, useRef, useState } from "react";

type WsEvent =
  | { type: "connected"; payload: unknown }
  | { type: "message"; payload: { leadId: number; message: unknown } }
  | {
      type: "status_update";
      payload: { leadId: number; messageId: number; deliveryStatus: string; deliveredAt?: number; readAt?: number };
    }
  | { type: "lead_updated"; payload: { lead: unknown } }
  | { type: "typing"; payload: unknown }
  | { type: "error"; payload: unknown };

const wsUrlForLead = (leadId: number) => {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/ws/${leadId}`;
};

export type ChatRealtimeStatus = "idle" | "connecting" | "open" | "closed" | "error";

export const useChatRealtime = (input: {
  leadId: number;
  onMessage: (payload: { leadId: number; message: unknown }) => void;
  onStatusUpdate: (payload: { leadId: number; messageId: number; deliveryStatus: string; deliveredAt?: number; readAt?: number }) => void;
  onLeadUpdated?: (payload: { lead: unknown }) => void;
}) => {
  const [status, setStatus] = useState<ChatRealtimeStatus>("idle");
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const backoffMsRef = useRef(250);

  useEffect(() => {
    const connect = () => {
      if (wsRef.current) return;
      setStatus("connecting");

      const ws = new WebSocket(wsUrlForLead(input.leadId));
      wsRef.current = ws;

      ws.onopen = () => {
        backoffMsRef.current = 250;
        setStatus("open");
      };

      ws.onmessage = (evt) => {
        try {
          const parsed = JSON.parse(String(evt.data)) as WsEvent;
          if (parsed.type === "message") input.onMessage(parsed.payload);
          if (parsed.type === "status_update") input.onStatusUpdate(parsed.payload);
          if (parsed.type === "lead_updated") input.onLeadUpdated?.(parsed.payload);
        } catch {
          // ignore
        }
      };

      ws.onerror = () => {
        setStatus("error");
      };

      ws.onclose = () => {
        wsRef.current = null;
        setStatus("closed");

        if (document.visibilityState === "hidden") return;

        const delay = Math.min(backoffMsRef.current, 15000);
        backoffMsRef.current = Math.min(backoffMsRef.current * 2, 15000);
        reconnectTimerRef.current = window.setTimeout(connect, delay);
      };
    };

    connect();

    const onVisibility = () => {
      if (document.visibilityState === "visible") connect();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [input.leadId]);

  return { status };
};
